import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { API } from '../constants';
import {
    ACCOUNTING_AREA_ID_MARKER,
    processRequestForAccountingAreaWithReplacement,
} from './network/managedRequestForAccountingArea';
import { RoleAddUserCardValues } from '../components/realms/roles-add-user-card';
import { PbRequest } from './pbRequest';
import { PbDispatch } from './index';

export enum UserSetupProcessRole {
    TECHNICIAN = 'TECHNICIAN',
    B2B_LAUNDRY_USAGE = 'B2B_LAUNDRY_USAGE',
}

export function hasSubRoles(role: UserSetupProcessRole): boolean {
    return isB2bLaundryUsage(role);
}

export function requiresPaymentConfirmation(
    role: UserSetupProcessRole
): boolean {
    return isB2bLaundryUsage(role);
}

function isB2bLaundryUsage(role: UserSetupProcessRole): boolean {
    return role === UserSetupProcessRole.B2B_LAUNDRY_USAGE;
}

export enum UserSetupProcessSubRole {
    RECEPTIONIST = 'RECEPTIONIST',
    INTERNAL = 'INTERNAL',
    HOUSEKEEPING = 'HOUSEKEEPING',
}

export const SUB_ROLES_FOR_INTERAL = [
    UserSetupProcessSubRole.RECEPTIONIST,
    UserSetupProcessSubRole.INTERNAL,
    UserSetupProcessSubRole.HOUSEKEEPING,
];
export const SUB_ROLES_FOR_EXTERNAL = [
    UserSetupProcessSubRole.RECEPTIONIST,
    UserSetupProcessSubRole.INTERNAL,
];

export enum UserSetupProcessStatus {
    COMPLETED,
    IN_PROGRESS,
    ERROR,
}

export interface UserSetupProcess {
    id?: string | number;
    email: string;
    role: UserSetupProcessRole;
    sub_role?: UserSetupProcessSubRole;
    status: UserSetupProcessStatus;
}

export interface UserSetupProcesses {
    setup_processes: UserSetupProcess[];
}

interface AccountingAreaRolesStore {
    invitesForAccountingArea?: UserSetupProcesses;
}

interface PostInviteForAccountingArea {
    email: string;
    role: UserSetupProcessRole;
    sub_role?: UserSetupProcessSubRole;
    payment_via_credit_note_accepted?: boolean;
}

const initialState: AccountingAreaRolesStore = {
    invitesForAccountingArea: undefined,
};

const accountingAreaRolesSlice = createSlice({
    name: 'accountingAreaRoles',
    initialState,
    reducers: {
        setInvitesForAccountingArea(
            state: AccountingAreaRolesStore,
            action: PayloadAction<UserSetupProcesses>
        ) {
            state.invitesForAccountingArea = action.payload;
        },
    },
});

export function CreateNewAccountingAreaInviteForEmail(
    formValues: RoleAddUserCardValues
) {
    return async (dispatch: PbDispatch) => {
        try {
            const invite: PostInviteForAccountingArea = {
                email: formValues.email,
                role: formValues.role,
                sub_role: hasSubRoles(formValues.role)
                    ? formValues.subRole
                    : undefined,
                payment_via_credit_note_accepted: requiresPaymentConfirmation(
                    formValues.role
                )
                    ? formValues.paymentConfirmation
                    : undefined,
            };
            const request = {
                method: 'POST',
                body: JSON.stringify(invite),
            };
            await processRequestForAccountingAreaWithReplacement(
                dispatch,
                `${API}/${ACCOUNTING_AREA_ID_MARKER}/role/activation`,
                request,
                PbRequest.POST_INVITE_FOR_ACCOUNTING_AREA
            );
        } catch {
            // Already handled
        }
    };
}

export function FetchInvitesForAccountingArea() {
    return async (dispatch: PbDispatch) => {
        try {
            const request = {
                method: 'GET',
            };
            const result =
                await processRequestForAccountingAreaWithReplacement<UserSetupProcesses>(
                    dispatch,
                    `${API}/${ACCOUNTING_AREA_ID_MARKER}/role/activation`,
                    request,
                    PbRequest.FETCH_INVITES_FOR_ACCOUNTING_AREA
                );
            dispatch(
                accountingAreaRolesSlice.actions.setInvitesForAccountingArea(
                    result
                )
            );
        } catch {}
    };
}

export function DeleteInviteForAccountingArea(processId: number) {
    return async (dispatch: PbDispatch) => {
        try {
            const request = {
                method: 'DELETE',
            };
            await processRequestForAccountingAreaWithReplacement(
                dispatch,
                `${API}/${ACCOUNTING_AREA_ID_MARKER}/role/activation/${processId}`,
                request,
                PbRequest.DELETE_INVITE_FOR_ACCOUNTING_AREA
            );

            dispatch(FetchInvitesForAccountingArea());
        } catch {}
    };
}

export default accountingAreaRolesSlice.reducer;
